import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTopButton from "@/components/feature/ScrollTopButton";
import ComicCard from "@/components/base/ComicCard";
import { comics, getComicById } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || "");

  const related = useMemo(() => {
    if (!comic) return [];
    return comics.filter((c) => c.id !== comic.id && c.category === comic.category).slice(0, 5);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="font-heading text-xl text-foreground-950">未找到该漫画</h1>
          <p className="text-sm text-foreground-600">这部作品可能已下架或链接有误。</p>
          <Link to="/list" className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
            返回漫库
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = Array.from({ length: Math.min(comic.episodes, 20) }, (_, i) => comic.episodes - i);

  const ld = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: comic.title,
    alternateName: comic.altTitle,
    url: `https://panelplay1.github.io/comic/${comic.id}`,
    description: comic.description,
    genre: comic.category,
    keywords: comic.tags.join(","),
    author: { "@type": "Organization", name: comic.author },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: 3200,
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://panelplay1.github.io/" },
      { "@type": "ListItem", position: 2, name: "漫库", item: "https://panelplay1.github.io/list" },
      { "@type": "ListItem", position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <nav className="mb-6 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/list" className="hover:text-primary-600">漫库</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/list?category=${comic.category}`} className="hover:text-primary-600">{comic.category}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{comic.title}</span>
        </nav>

        <section className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          <div>
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <img
                src={comic.cover}
                alt={`${comic.title}漫画封面 - 天天漫画网`}
                title={`${comic.title} ${comic.latestChapter}`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex gap-2">
              <Link
                to="/list"
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-book-2-fill"></i>立即开看
              </Link>
              <Link
                to="/list"
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-primary-400 px-4 py-2.5 text-sm font-bold text-primary-600 transition-colors hover:bg-primary-50"
              >
                <i className="ri-add-line"></i>追更收藏
              </Link>
            </div>
          </div>

          <div>
            <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">{comic.title}</h1>
            <p className="mt-1 text-sm text-foreground-500">别名：{comic.altTitle}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full px-3 py-1 font-bold ${comic.status === "连载中" ? "bg-accent-100 text-accent-700" : "bg-foreground-200 text-foreground-700"}`}>
                {comic.status}
              </span>
              {comic.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">{t}</span>
              ))}
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1 text-foreground-600">
                <i className="ri-star-fill text-secondary-500"></i>{comic.rating.toFixed(1)} 分
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-4">
              {[
                { k: "作者", v: comic.author },
                { k: "总话数", v: `${comic.episodes} 话` },
                { k: "题材", v: comic.category },
                { k: "最近更新", v: comic.updateTime },
              ].map((it) => (
                <div key={it.k}>
                  <dt className="text-xs text-foreground-500">{it.k}</dt>
                  <dd className="mt-1 text-sm font-bold text-foreground-900">{it.v}</dd>
                </div>
              ))}
            </dl>

            <section className="mt-6">
              <h2 className="text-sm font-bold text-foreground-900">作品简介</h2>
              <p className="mt-3 leading-relaxed text-foreground-700">{comic.description}</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                天天漫画网每日更新《{comic.title}》最新话，连载漫画抢先看。收藏本作即可第一时间收到更新提醒，追更漫画不迷路。
              </p>
            </section>

            <section className="mt-8">
              <h2 className="mb-4 text-sm font-bold text-foreground-900">章节列表</h2>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {chapters.map((n) => (
                  <span
                    key={n}
                    className="truncate rounded-md border border-background-200 bg-background-50 px-3 py-2 text-center text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {n === comic.episodes ? comic.latestChapter : `第${n}话`}
                  </span>
                ))}
              </div>
            </section>
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-14">
            <div className="mb-6">
              <p className="mb-2 flex items-center gap-2 text-xs font-bold tracking-widest text-primary-500 uppercase">
                相关推荐
                <span className="h-px w-6 bg-primary-400" />
              </p>
              <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">
                同题材 · 追更漫画推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollTopButton />
    </>
  );
}